import type { Outcome } from "shared/components/vega/LumenV2/utils";
import type { Vero1ChangedItems } from "shared/interfaces/api/vero1";
import { Vero1Recommendation } from "shared/interfaces/api/vero1";

import { ButtonType } from "./interfaces";

/* PRODUCT-2779 (revised 2026-09-14): the strip surfaces only the verdict's own
   severity. A Decline shows fail items, a Conditional shows warn items, an
   Approve shows what the analyst moved to pass; anything at another severity
   keeps its place on the factor cards and the Deal review tab. */
export type Severity = "pass" | "warn" | "fail";

const RECOMMENDATION_SEVERITY: Record<string, Severity> = {
  APPROVE: "pass",
  CONDITIONAL: "warn",
  DECLINE: "fail",
};

export const recommendationSeverity = (
  recommendation: string | null | undefined
): Severity | null =>
  recommendation ? RECOMMENDATION_SEVERITY[recommendation] ?? null : null;

/* PRODUCT-2623 AC "the decision button matches the verdict": the ActionBar's
   primary CTA rides the same severity as the strip, so both surfaces agree on
   what a submitted VERO1 recommendation means. CANCEL is a process outcome
   with no severity (spec §01) — kept as the single special case rather than
   inventing a fourth Severity value. AGREE / unknown fall through as null so
   the caller can defer to the Lumen-derived button. */
const SEVERITY_TO_BUTTON: Record<Severity, ButtonType> = {
  pass: ButtonType.approve,
  warn: ButtonType.conditional,
  fail: ButtonType.decline,
};

export const severityDecisionButton = (
  severity: Severity | null
): ButtonType | undefined =>
  severity ? SEVERITY_TO_BUTTON[severity] : undefined;

export const recommendationDecisionButton = (
  recommendation: string | null | undefined
): ButtonType | undefined => {
  if (recommendation === Vero1Recommendation.Cancel) return ButtonType.cancel;
  return severityDecisionButton(recommendationSeverity(recommendation));
};

/* The verdict's colour on a raw run: Lumen writes it at run level; a run that
   predates that carries it on the band sharing the verdict's label (label
   matching is safe on raw runs — derived runs never reach this path). */
export const runVerdictColor = (
  run:
    | { scoreLabel?: string; scoreLabelColor?: string; outcomes?: unknown }
    | null
    | undefined,
  bands: Outcome[]
): Severity | null => {
  const own = run?.scoreLabelColor;
  if (own === "pass" || own === "warn" || own === "fail") return own;
  const byLabel = bands.find(
    (band) => band.scoreLabel && band.scoreLabel === run?.scoreLabel
  )?.scoreLabelColor;
  return byLabel === "pass" || byLabel === "warn" || byLabel === "fail"
    ? byLabel
    : null;
};

/* Bands at the verdict's severity only. With no resolvable verdict colour the
   list is left whole rather than blanked. */
export const scopeBands = (
  bands: Outcome[],
  color: Severity | null
): Outcome[] =>
  color ? bands.filter((band) => band.scoreLabelColor === color) : bands;

/* The items whose submitted result sits at the recommendation's severity — a
   pick to that severity, or an annotation on a row Lumen already had there. */
export const scopeChangedItems = (
  data: Vero1ChangedItems,
  severity: Severity | null
): Vero1ChangedItems => ({
  ...data,
  items: severity
    ? data.items.filter(
        (item) => (item.overrideSeverity ?? item.originalSeverity) === severity
      )
    : [],
});
